import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import Layout from "../common/Layout";
import { track } from "../features/analytics/tracker";
import { usePageMeta, useJsonLd } from "../common/usePageMeta";

const FAQS = [
  {
    q: "How fresh is it, really?",
    a: "Everything is harvested the same day it's delivered, usually within a couple of hours of being cut. Nothing sits in cold storage, ever.",
  },
  {
    q: "Do I need to wash it before eating?",
    a: "The plants are grown off the ground in clean, filtered water with no pesticides, so there's no soil or spray residue. As with any fresh produce, a quick rinse before eating is always a good habit.",
  },
  {
    q: "How long will it keep in the fridge?",
    a: "Because it's cut hours (not weeks) before it reaches you, most of my greens keep 7–14 days in a sealed bag in the fridge. Noticeably longer than supermarket equivalents. Herbs like basil prefer room temperature rather than the fridge to stay fresher for longer!",
  },
  {
    q: "Where do you deliver?",
    a: "Hockley and the surrounding Essex area: Rochford, Hawkwell, Rayleigh and nearby. If you're close but not sure, get in touch and I'll tell you straight away.",
  },
  {
    q: "When will my order arrive?",
    a: "There are two delivery windows: late morning (10:30am–1:00pm) and early evening (5:30–7:00pm). Order before 10am and it usually goes out the same morning; later orders move to the evening or the next available window.",
  },
  {
    q: "Is it organic?",
    a: "It's not certified organic; hydroponic growing sits outside the UK certification scheme. But there are zero chemical pesticides: ladybirds handle pest control, and the plants are fed a precisely balanced nutrient solution instead of relying on soil.",
  },
  {
    q: "Do you do subscriptions?",
    a: "Yes, you can set up a regular box so fresh greens just turn up each week. See the Subscribe page for options.",
  },
];

const steps = [
{
    key: "flavor_first",
    icon: "🌿",
    title: "Grown for Flavour, Not Travel",
    description: "Supermarket greens are bred to survive 1,000-mile lorry journeys. Mine are bred for taste. By giving plants a perfect summer day 24/7, they produce more essential oils and thinner, more tender leaves. You’ll smell the difference the moment you open the bag."
  },
  {
    key: "ladybug_defense",
    icon: "🐞",
    title: "Protected by Ladybirds",
    description: "I don’t use chemical pesticides - instead, I use biocontrols. I release friendly ladybirds into the farm to keep the ecosystem balanced and the plants healthy. It’s nature’s way of keeping your food as clean as it is fresh."
  },
  {
    key: "hydro_nutrition",
    icon: "🧪",
    title: "Precision Nutrition",
    description: "Instead of hoping for good soil, I personally balance the water’s pH and nutrients every day. This 'direct feeding' means the plants never stress out, resulting in a significantly longer shelf-life in your fridge."
  }
];

const fadeItem = { hidden:{opacity:0, y:12}, show:{opacity:1, y:0, transition:{duration:.25}} };

// ─── Simulated grow-room telemetry ──────────────────────────────────────────
// Representative of a typical day in the farm. Values drift smoothly through
// the day so the page feels alive, without connecting to any farm systems.

const smoothDrift = (seed, amplitude) => {
  const t = Date.now() / 60000; // minutes
  return Math.sin(t / 7 + seed) * amplitude + Math.sin(t / 2.3 + seed * 3) * amplitude * 0.4;
};

function readFarmConditions() {
  const hour = new Date().getHours();
  // Lights run midnight → 4pm: cheaper overnight energy, same 16h "summer day"
  const isDay = hour >= 0 && hour < 16;

  return {
    isDay,
    temp:     (isDay ? 22.4 : 18.3) + smoothDrift(1, 0.25),
    humidity: (isDay ? 55   : 65)   + smoothDrift(2, 1.2),
    waterTemp: 19.8 + smoothDrift(3, 0.3),
    ph:        5.9  + smoothDrift(4, 0.06),
    ec:        1.8  + smoothDrift(5, 0.05),
  };
}

// What's growing right now - day-in-cycle advances daily, offset per crop so
// the board always shows a realistic mix of stages.
const GROW_CYCLES = [
  { name: "Genovese Basil", icon: "🌿", days: 28, offset: 25 },
  { name: "Pea Shoots",     icon: "🌱", days: 14, offset: 5 },
  { name: "Wild Rocket",    icon: "🥬", days: 24, offset: 10 },
  { name: "Coriander",      icon: "🍃", days: 26, offset: 19 },
  { name: "Garden Cress",   icon: "🌾", days: 12, offset: 3 },
];

function growBoard() {
  const start = new Date(new Date().getFullYear(), 0, 0);
  const dayOfYear = Math.floor((Date.now() - start.getTime()) / 86400000);

  return GROW_CYCLES.map((c) => {
    const day = ((dayOfYear + c.offset) % c.days) + 1;
    const pct = Math.round((day / c.days) * 100);
    const stage =
      pct >= 90 ? { label: "Harvesting this week", color: "#4CAF50" } :
      pct >= 60 ? { label: "Filling out",          color: "#8bc34a" } :
      pct >= 25 ? { label: "Growing fast",         color: "#03a9f4" } :
                  { label: "Just germinated",      color: "#38bdf8" };
    return { ...c, day, pct, stage };
  });
}

// Today's rhythm in the grow room, marked off as the day goes on
function todaysJobs() {
  const now = new Date();
  const hour = now.getHours() + now.getMinutes() / 60;
  const weekday = now.getDay(); // 0 Sun … 6 Sat

  const jobs = [
    { at: 0,    time: "12:00am", icon: "💡", label: "Lights on, the plants' sunrise" },
    { at: 8,    time: "8:00am",  icon: "🧪", label: "pH & nutrient check on the reservoir" },
    { at: 9,    time: "9:00am",  icon: "✂️", label: "Harvest & pack for the morning round" },
    { at: 10.5, time: "10:30am", icon: "🚗", label: "Morning deliveries go out" },
    { at: 14,   time: "2:00pm",  icon: "🐞", label: "Ladybird patrol & plant health walk" },
    { at: 16,   time: "4:00pm",  icon: "🌙", label: "Lights off, plants rest" },
    { at: 17.5, time: "5:30pm",  icon: "🚗", label: "Evening deliveries go out" },
  ];

  if (weekday === 1 || weekday === 4) {
    jobs.splice(2, 0, { at: 8.75, time: "8:45am", icon: "🌱", label: "Seeding day - fresh trays into germination" });
  }
  if (weekday === 3) {
    jobs.splice(2, 0, { at: 8.75, time: "8:45am", icon: "🪴", label: "Transplant day - seedlings move to the benches" });
  }

  return jobs.map((j) => ({
    ...j,
    status: hour >= j.at + 1 ? "done" : hour >= j.at ? "now" : "later",
  }));
}

export default function HowItWorksPage() {
  const seen = useRef(new Set());
  const [farm, setFarm] = useState(() => ({
    ...readFarmConditions(),
    crops: growBoard(),
    jobs: todaysJobs(),
  }));

  useEffect(() => {
    const tick = () =>
      setFarm({ ...readFarmConditions(), crops: growBoard(), jobs: todaysJobs() });
    const timer = setInterval(tick, 8000);
    return () => clearInterval(timer);
  }, []);

  const farmData = {
    isDay: farm.isDay,
    temp: farm.temp.toFixed(1),
    humidity: farm.humidity.toFixed(0),
  };

  const markSeen = (event, payload = {}) => {
    const key = `${event}:${JSON.stringify(payload)}`;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    track(event, payload);
  };

  useEffect(() => { track("hiw_view"); }, []);

  usePageMeta({
    title: "How It Works - Indoor Hydroponic Farm in Hockley | TearDrop Hydro",
    description:
      "See inside the grow room: how herbs, salads and microgreens are grown hydroponically in Hockley - no soil, no pesticides, harvested hours before delivery.",
    path: "/how-it-works",
  });
  useJsonLd("faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  return (
    <Layout>
      {/* HERO */}
      <section style={{ position: "relative", overflow: "hidden", minHeight: "clamp(380px, 55svh, 620px)", display: "flex", alignItems: "center" }}>
        <img
          src="/images/products/seedlings/IMG_4847.JPEG"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 30%" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, rgba(10,28,20,.82) 0%, rgba(10,28,20,.52) 60%, transparent 100%)" }} />
        <div className="container" style={{ position: "relative", zIndex: 1, padding: "var(--s-10) var(--s-5)" }}>
          <div className={`farm-status-pill ${!farmData.isDay ? "status-sleep" : ""}`} style={{ marginBottom: "var(--s-6)" }}>
            <div className="status-inner">
              <div className="status-label">
                <div className="pulse-dot" />
                <strong>Farm: {farmData.isDay ? "Active" : "Sleeping"}</strong>
              </div>
              <span>🌡️ {farmData.temp}°C</span>
              <span>💧 {farmData.humidity}% Humidity</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-3)" }}>How I Do It</p>
            <h1 className="h1" style={{ color: "#fff", maxWidth: 580, marginTop: 0, marginBottom: "var(--s-4)", fontSize: "clamp(28px,5vw,44px)", lineHeight: 1.1 }}>
              Controlled from seed to harvest. Same result, every time.
            </h1>
            <p style={{ color: "rgba(255,255,255,.8)", fontSize: "clamp(15px,1.8vw,18px)", maxWidth: 500, lineHeight: 1.6 }}>
              I’ve moved the growing process fully indoors, removing almost all the variables. No soil, no chemicals. Just water, light, and the right conditions.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container section hiw">

        {/* Steps */}
        <ol className="hiw-steps" role="list">
          {steps.map((s, i) => (
            <motion.li
              key={s.key}
              className="hiw-card"
              initial="hidden"
              whileInView="show"
              viewport={{ once:true, amount:.35 }}
              variants={fadeItem}
              onViewportEnter={() => markSeen("hiw_step_visible", { step: s.key, index: i })}
            >
              <span className="step-emoji" aria-hidden="true">{s.icon}</span>
              <h2 className="h3">{s.title}</h2>
              <p className="muted">{s.description}</p>
            </motion.li>
          ))}
        </ol>

        {/* LIVE FARM PANEL */}
        <motion.section
          className="live-farm"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={fadeItem}
          onViewportEnter={() => markSeen("hiw_section_visible", { section: "live_farm" })}
        >
          <div style={{ textAlign: "center" }}>
            <p className="eyebrow" style={{ color: "var(--accent)", marginBottom: "var(--s-2)" }}>
              Inside The Grow Room
            </p>
            <h2 className="h3" style={{ marginTop: 0 }}>What the farm is doing right now</h2>
            <p className="muted" style={{ maxWidth: 560, margin: "0 auto" }}>
              The farm runs on a 16-hour "perfect summer day", timed overnight to use
              cheaper, greener energy. Here's a window into that routine.
            </p>
          </div>

          {/* Sensor tiles */}
          <div className="lf-tiles">
            <div className="lf-tile">
              <span className="lf-tile__icon">{farm.isDay ? "💡" : "🌙"}</span>
              <strong>{farm.isDay ? "Lights on" : "Lights off"}</strong>
              <span className="muted">{farm.isDay ? "Photosynthesis mode" : "Rest cycle until midnight"}</span>
            </div>
            <div className="lf-tile">
              <span className="lf-tile__icon">🌡️</span>
              <strong>{farm.temp.toFixed(1)}°C</strong>
              <span className="muted">Air temperature</span>
            </div>
            <div className="lf-tile">
              <span className="lf-tile__icon">💧</span>
              <strong>{farm.humidity.toFixed(0)}%</strong>
              <span className="muted">Humidity</span>
            </div>
            <div className="lf-tile">
              <span className="lf-tile__icon">🚿</span>
              <strong>{farm.waterTemp.toFixed(1)}°C</strong>
              <span className="muted">Water temperature</span>
            </div>
            <div className="lf-tile">
              <span className="lf-tile__icon">⚗️</span>
              <strong>pH {farm.ph.toFixed(2)}</strong>
              <span className="muted">Sweet spot: 5.8–6.2</span>
            </div>
            <div className="lf-tile">
              <span className="lf-tile__icon">⚡</span>
              <strong>{farm.ec.toFixed(2)} EC</strong>
              <span className="muted">Nutrient strength</span>
            </div>
          </div>

        </motion.section>

                {/* RENEWABILITY & ECO SECTION */}
        <motion.section
          className="eco-impact"
          initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeItem}
        >
          <div className="hiw-card eco-card">
            <h2 className="h3 eco-card__heading">The Environmental Numbers</h2>
            <p className="muted eco-card__sub">Growing indoors uses far fewer resources than conventional farming. The numbers are genuinely good.</p>
            <div className="eco-grid">
              <div className="eco-item">
                <h4 className="eco-item__heading">💧 90% Less Water</h4>
                <p className="muted">My closed-loop system recycles every drop. Traditional farming loses huge amounts to evaporation and soil runoff.</p>
              </div>
              <div className="eco-item">
                <h4 className="eco-item__heading">🚚 3 Miles, Not 1,000</h4>
                <p className="muted">The average supermarket salad travels 1,000 miles. Mine travels 3. Less fuel, less carbon, more flavour.</p>
              </div>
              <div className="eco-item">
                <h4 className="eco-item__heading">♻️ Peat-Free & Renewable</h4>
                <p className="muted">I grow in coconut coir (a byproduct of the coconut industry) powered entirely by renewable energy.</p>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section 
          className="hiw-comparison-v2"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeItem}
          onViewportEnter={() => markSeen("hiw_section_visible", { section: "comparison" })}
        >
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <h2 className="h3">The TearDrop Difference</h2>
            <p className="muted">Why choosing local hydroponics matters</p>
          </div>

          <div className="comparison-grid">
            {/* Left Column: The "Old Way" */}
            <div className="comp-column bad">
              <div className="comp-header comp-header--bad">Standard Supermarket</div>
              <ul className="comp-list">
                <li className="comp-item">
                  <span className="comp-icon">❌</span>
                  <span className="muted">Harvested weeks ago and kept in cold storage.</span>
                </li>
                <li className="comp-item">
                  <span className="comp-icon">❌</span>
                  <span className="muted">Bred to survive transport, not for flavour.</span>
                </li>
                <li className="comp-item">
                  <span className="comp-icon">❌</span>
                  <span className="muted">Chlorine baths to kill soil-borne bacteria.</span>
                </li>
              </ul>
            </div>

            {/* Right Column: Your Way */}
            <div className="comp-column good">
              <div className="comp-header comp-header--good">TearDrop Hydro</div>
              <ul className="comp-list">
                <li className="comp-item">
                  <span className="comp-icon">✅</span>
                  <strong>Harvested 2 hours before it reaches your door.</strong>
                </li>
                <li className="comp-item">
                  <span className="comp-icon">✅</span>
                  <strong>Perfect stress-free climate = maximum essential oils.</strong>
                </li>
                <li className="comp-item">
                  <span className="comp-icon">🐞</span>
                  <strong>Protected by ladybirds, not chemicals.</strong>
                </li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* Delivery Schedule Section */}
        <motion.section
          className="hiw-delivery"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          variants={fadeItem}
        >
          <div className="hiw-delivery__inner">
            <h2 className="h3">Reliable Local Delivery</h2>
            <p className="muted hiw-delivery__sub">
              I harvest and deliver personally, so that greens never sit in a van for more than 2 hours.
            </p>
            <div className="hiw-delivery-windows">
              <div className="hiw-window-card">
                <strong>Late Morning</strong>
                <div className="hiw-window-time">10:30am – 1:00pm</div>
              </div>
              <div className="hiw-window-card">
                <strong>Early Evening</strong>
                <div className="hiw-window-time">5:30pm – 7:00pm</div>
              </div>
            </div>
            <p className="muted" style={{ fontSize: "var(--fs-13)" }}>
              <strong>Order cut-off:</strong> Orders placed before 10:00am usually go out the same morning.
              Later orders move to the evening slot or the next available window.
            </p>
          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          className="hiw-faq"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={fadeItem}
          onViewportEnter={() => markSeen("hiw_section_visible", { section: "faq" })}
        >
          <div style={{ textAlign: "center", marginBottom: "var(--s-4)" }}>
            <h2 className="h3">Questions people actually ask</h2>
            <p className="muted">The honest answers, straight from the grower.</p>
          </div>
          <ul className="faq-list" role="list" style={{ maxWidth: 720, margin: "0 auto" }}>
            {FAQS.map((f, i) => (
              <li key={i} className="faq-item">
                <details onToggle={(e) => { if (e.target.open) markSeen("hiw_faq_open", { q: f.q }); }}>
                  <summary>
                    <span>{f.q}</span>
                    <ChevronDown className="faq-chevron" size={18} aria-hidden="true" />
                  </summary>
                  <p className="muted" style={{ marginTop: "var(--s-2)", marginBottom: 0, lineHeight: 1.6 }}>{f.a}</p>
                </details>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* CTA */}
        <section className="hiw-cta">
          <div className="cta-card">
            <div>
              <h2 className="h3">Taste the difference</h2>
              <p className="muted">Join the locals getting their greens straight from the grower, harvested the same morning they're delivered.</p>
            </div>
            <div className="cta-row">
              <Link to="/browse-products" className="btn btn-primary" onClick={() => track("hiw_cta_click", { cta: "shop_now" })}>
                Browse Today's Harvest
              </Link>
              {/* <Link to="/partner" className="btn btn-ghost" onClick={() => track("hiw_cta_click", { cta: "partner" })}>
                Partner with me
              </Link> */}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}